import { Component, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { StyleClassModule } from 'primeng/styleclass';
import { TooltipModule } from 'primeng/tooltip';
import { LayoutService } from '../../core/services/conf/layout.service';
import { ConfiguratorComponent } from '../configurator/configurator.component';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../core/services/api/auth.service';
import { NotificationService } from '../../shared/components/notification-modal/notification.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { RoleService } from '../../modules/settings/core/services/api/role.service';
import { HelpContentService } from '../../core/services/conf/help-content.service';
import { MARKETING_ROLES } from '../../core/models/constants/roles.constants';

interface BranchOption {
    idBranch: number;
    name: string;
    code: string;
}

interface RoleOption {
    code: string;
    name: string;
}

@Component({
    selector: 'app-topbar',
    imports: [RouterModule, StyleClassModule, ConfiguratorComponent, TooltipModule, DialogModule, ButtonModule, SelectModule, FormsModule],
    templateUrl: './topbar.component.html',
    styleUrl: './topbar.component.scss'
})
export class TopbarComponent implements OnInit {
    items!: MenuItem[];
    private authService = inject(AuthService);
    private notifier = inject(NotificationService);
    rolUser = this.authService.getUserRole();
    user: boolean = false;
    displayConfirmation: boolean = false;
    nombreUsuario: string = '';
    sucursal: string = '';

    canSwitchBranch: boolean = false;
    branches: BranchOption[] = [];
    selectedBranch: BranchOption | null = null;
    switchingBranch: boolean = false;

    // ── Preview de roles ─────────────────────────────────────────────────────
    canPreviewRole: boolean = false;
    availableRoles: RoleOption[] = [];
    selectedPreviewRole: string | null = null;
    private roleService = inject(RoleService);
    isMobile: boolean = false;

    // ── Ayuda ────────────────────────────────────────────────────────────────
    private helpContentService = inject(HelpContentService);
    helpAvailable: boolean = false;

    get canShowHelp(): boolean {
        return this.user && this.helpAvailable;
    }

    openHelp(): void {
        this.helpContentService.requestTour();
    }

    constructor(
        public layoutService: LayoutService,
        private router: Router,
        private breakpointObserver: BreakpointObserver
    ) {
        const roleUser = !this.authService.hasRole('CUSTOMER');

        console.log(this.rolUser);

        this.user = roleUser;
        if (!this.user) {
            this.layoutService.onMenuToggle();
        }
    }

    ngOnInit() {
        this.nombreUsuario = 'Bienvenido/a ' + this.authService.getCurrentUser()?.firstName + ' ' + this.authService.getCurrentUser()?.lastName || '';
        this.sucursal = this.authService.getCurrentUser()?.branch || 'ninguna';

        this.canSwitchBranch = this.authService.hasRole('ROOT') || this.authService.hasRole('ADMIN') || this.authService.hasRole('COBRANZA') || this.authService.hasAnyRole(MARKETING_ROLES);
        if (this.canSwitchBranch) {
            this.loadBranches();
        }

        this.breakpointObserver.observe(['(max-width: 900px)']).subscribe((result) => {
            this.isMobile = result.matches;
        });
        this.canPreviewRole = this.authService.hasRole('ROOT');
        if (this.canPreviewRole) {
            this.loadRoles();
        }

        // ── Ayuda: mostrar el botón solo si la vista actual tiene ayuda disponible ──
        this.updateHelpAvailability();
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => this.updateHelpAvailability());
    }

    private updateHelpAvailability(): void {
        this.helpAvailable = this.helpContentService.getHelpForUrl(this.router.url) != null;
    }

    private loadRoles(): void {
        this.roleService.list().subscribe({
            next: (res) => {
                const roles = res?.data ?? [];
                this.availableRoles = roles.map((r: any) => ({
                    code: r.code,
                    name: r.name || r.code
                }));
            },
            error: () => {
                this.availableRoles = [];
            }
        });
    }

    onPreviewRoleChange(): void {
        this.authService.setPreviewRole(this.selectedPreviewRole);
        const url = this.router.url;
        this.router.navigateByUrl('/sys', { skipLocationChange: true }).then(() => {
            this.router.navigateByUrl(url, { skipLocationChange: true });
        });
    }

    private loadBranches(): void {
        this.authService.getActiveBranches().subscribe({
            next: (res: any) => {
                const list: any[] = res?.object ?? [];
                this.branches = list.map((b: any) => ({
                    idBranch: b.idBranch,
                    name: b.name,
                    code: b.code
                }));
                const currentBranch = this.authService.getCurrentUser()?.branch;
                if (currentBranch) {
                    this.selectedBranch = this.branches.find((b) => b.name === currentBranch) ?? null;
                }
            },
            error: () => {
                this.branches = [];
            }
        });
    }

    onBranchChange(): void {
        if (!this.selectedBranch || this.switchingBranch) return;
        const target = this.selectedBranch;
        const currentBranch = this.authService.getCurrentUser()?.branch;
        if (target.name === currentBranch) return;

        this.switchingBranch = true;
        this.authService.switchBranch(target.idBranch).subscribe({
            next: (response: any) => {
                this.switchingBranch = false;
                this.sucursal = this.authService.getCurrentUser()?.branch || target.name;
                if (response.data?.branchReassigned) {
                    this.notifier.info(`🏢 Reasignado a sucursal "${response.data?.newBranchName || target.name}"`, '', 4000);
                }
                setTimeout(() => window.location.reload(), 1500);
            },
            error: () => {
                this.switchingBranch = false;
                this.selectedBranch = this.branches.find((b) => b.name === this.authService.getCurrentUser()?.branch) ?? null;
            }
        });
    }

    toggleDarkMode() {
        this.layoutService.layoutConfig.update((state) => ({
            ...state,
            darkTheme: !state.darkTheme
        }));
    }

    goto(ruta: string) {
        this.router.navigate([ruta]);
    }

    openConfirmation() {
        this.displayConfirmation = true;
    }

    closeConfirmation(confirmation: boolean) {
        if (confirmation) {
            this.authService.logout();
        }
        this.displayConfirmation = false;
    }
}
